import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { InfoPopover } from "../gpt/evidence/InfoPopover";

// A [0,1] score → a 1–4 signal strength (like phone bars). value is non-null
// here (callers skip a null/unknown score rather than draw empty bars).
function signalLevel(value: number): number {
  if (value >= 0.75) return 4;
  if (value >= 0.5) return 3;
  if (value >= 0.25) return 2;
  return 1;
}

// Colour band by score: green (strong) → orange (weak). `fill` is the filled-bar
// colour; `bg`/`text` dress the chip / percentage so the whole thing reads at a
// glance.
function signalBand(value: number): {
  fill: string;
  bg: string;
  text: string;
} {
  if (value >= 0.75)
    return {
      fill: "bg-emerald-500",
      bg: "bg-emerald-50",
      text: "text-emerald-700",
    };
  if (value >= 0.5)
    return { fill: "bg-lime-500", bg: "bg-lime-50", text: "text-lime-700" };
  if (value >= 0.25)
    return { fill: "bg-amber-500", bg: "bg-amber-50", text: "text-amber-700" };
  return { fill: "bg-orange-500", bg: "bg-orange-50", text: "text-orange-700" };
}

const BAR_HEIGHTS = ["h-1.5", "h-2", "h-2.5", "h-3"] as const;

function SignalBars({ level, fill }: { level: number; fill: string }) {
  return (
    <span className="inline-flex items-end gap-0.5" aria-hidden>
      {BAR_HEIGHTS.map((height, index) => (
        <span
          key={index}
          className={cn(
            "w-1 rounded-full",
            height,
            index < level ? fill : "bg-ds-slate-200"
          )}
        />
      ))}
    </span>
  );
}

// One metric row: label (+ info tooltip) on the left, the colour-banded
// signal-strength meter + percentage on the right. A null score reads "n/a".
export function SignalMetric({
  label,
  info,
  value,
}: {
  label: string;
  info: ReactNode;
  value: number | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="flex items-center gap-1 text-sm text-ds-slate-700">
        {label}
        <InfoPopover label={label} align="left">
          {info}
        </InfoPopover>
      </span>
      {value === null ? (
        <span className="text-sm font-medium text-ds-slate-400">
          {t("n/a")}
        </span>
      ) : (
        <span className="flex items-center gap-2">
          <SignalBars
            level={signalLevel(value)}
            fill={signalBand(value).fill}
          />
          <span
            className={cn(
              "min-w-9 text-right text-sm font-semibold",
              signalBand(value).text
            )}
          >
            {String(Math.round(value * 100))}%
          </span>
        </span>
      )}
    </div>
  );
}

export function SourceQualityExplanation() {
  const { t } = useTranslation();
  return (
    <>
      <span className="block">
        {t(
          "The stored quality score reflects the study design and journal standing when available."
        )}
      </span>
      <span className="mt-1 block">
        {t(
          "Without a stored score, only the study design determines the percentage."
        )}
      </span>
      <span className="mt-1 block">
        {t(
          "This percentage is not the probability that the answer is correct."
        )}
      </span>
    </>
  );
}

function Signal({ label, value }: { label: string; value: number }) {
  const band = signalBand(value);
  const pct = `${String(Math.round(value * 100))}%`;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded px-1.5 py-0.5",
        band.bg,
        band.text
      )}
      title={`${label}: ${pct}`}
    >
      <span className="text-xs font-medium">{label}</span>
      <SignalBars level={signalLevel(value)} fill={band.fill} />
      <span className="text-xs font-semibold">{pct}</span>
    </span>
  );
}

/**
 * Glanceable Confidence + Endorsement signals for one cited source (shown in the
 * end-of-answer source list). Each is a colour-banded signal-strength bar +
 * percentage. A score that is null (unknown — e.g. a web source with no quality)
 * is omitted rather than drawn as empty; nothing renders when both are unknown.
 */
export function SourceSignals({
  quality,
  endorsement,
}: {
  quality: number | null;
  endorsement: number | null;
}) {
  const { t } = useTranslation();
  if (quality === null && endorsement === null) return null;
  return (
    <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
      {quality !== null && (
        <Signal label={t("Source quality")} value={quality} />
      )}
      {endorsement !== null && (
        <Signal label={t("Endorsement")} value={endorsement} />
      )}
    </span>
  );
}
